import React, { useEffect, useRef, useState } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Button,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Box,
  Grid,
  Paper,
  Divider,
  Popover,
  Stack,
  styled,
  InputBase,
  Menu,
  MenuItem,
  Fade,
  InputAdornment,
} from "@mui/material";
import {
  Menu as MenuIcon,
  Search as SearchIcon,
  Directions as DirectionsIcon,
  WifiCalling3 as WifiCalling3Icon,
  Mail as MailIcon,
  ArrowDropDown as ArrowDropDownIcon,
} from "@mui/icons-material";
import BookingModal from "./BookingModal";
import { COLORS, EMAIL, PHONE_NUMBER } from "../constants/constants";
import { parseCsvFile } from "../utils/parseCsv";
import { useProductStore } from "../store/productStore";
import { useNavigate } from "react-router-dom";
import { useBlogStore } from "../store/blogStore";
import { getAllBlogPosts } from "../utils/firebaseBlogUtils";
import { Helmet } from "react-helmet";
import Zalo from "./Zalo";

interface Category {
  id: string;
  name: string;
  children?: Category[];
}

const categories: Category[] = [
  { id: "home", name: "Trang chủ" },
  { id: "blog", name: "Bài viết" },
  {
    id: "may-lanh",
    name: "Máy lạnh",
    children: [
      { id: "may-lanh-treo-tuong", name: "Treo tường" },
      { id: "may-lanh-am-tran", name: "Âm trần" },
      { id: "may-lanh-tu-dung", name: "Tủ đứng" },
      { id: "may-lanh-giau-tran-ong-gio", name: "Giấu trần ống gió" },
      { id: "may-lanh-ap-tran", name: "Áp trần" },
      { id: "he-thong-dieu-hoa-multi", name: "Multi" },
    ],
  },
  { id: "ve-sinh-may-lanh", name: "Vệ sinh máy lạnh" },
  { id: "bang-gia", name: "Bảng giá máy lạnh" },
];

const AnimatedButton = styled(Button)(({ theme }) => ({
  position: "relative",
  padding: "8px 10px",
  margin: "0 4px",
  minWidth: "auto",
  borderRadius: "4px",
  overflow: "hidden",
  transition: "all 0.2s ease",
  fontWeight: 500,
  "&:hover": {
    backgroundColor: "transparent",
    transform: "translateY(-1px)",
    "&::after": {
      width: "100%",
      left: 0,
    },
  },
  "&::after": {
    content: '""',
    position: "absolute",
    bottom: 0,
    left: "50%",
    width: 0,
    height: "2px",
    backgroundColor: "#00a0e4",
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  },
}));

const Header = () => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [currentCategory, setCurrentCategory] = useState(categories[0]);
  const [openBooking, setOpenBooking] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedCategory, setSelectedCategory] = useState<null | Category>(
    null
  );
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const navigate = useNavigate();
  const { setProducts } = useProductStore();
  const { setPosts } = useBlogStore();
  const searchRef = useRef<HTMLDivElement>(null);
  const headerDivRef = useRef<HTMLDivElement>(null);
  const secondDivRef = useRef<HTMLDivElement>(null);

  const handleOpenBooking = () => setOpenBooking(true);
  const handleCloseBooking = () => setOpenBooking(false);
  const handleDrawerToggle = () => setIsDrawerOpen(!isDrawerOpen);
  const handleSearchToggle = () => setIsSearchOpen(!isSearchOpen);

  const handleCategoryClick = (categoryId: string) => {
    const found =
      categories.find((cat) => cat.id === categoryId) ||
      categories[0].children?.find((child) => child.id === categoryId) ||
      categories[0];
    setCurrentCategory(found);
    setIsDrawerOpen(false);
    setMenuAnchorEl(null);
    window.scrollTo({ top: 0, behavior: "smooth" });

    if (categoryId === "home") navigate("/");
    else if (categoryId === "ve-sinh-may-lanh") navigate("/clearn");
    else if (categoryId === "bang-gia") navigate("/price");
    else if (categoryId === "blog") navigate("/blog/posts");
    else navigate(`/products?category=${categoryId}`);
  };

  const handleSearch = (input: string) => {
    navigate(`/products?search=${input}`);
    setCurrentCategory(categories[0]);
    setIsSearchOpen(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleMenuOpen = (
    event: React.MouseEvent<HTMLElement>,
    category: Category
  ) => {
    setMenuAnchorEl(event.currentTarget);
    setSelectedCategory(category);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setSelectedCategory(null);
  };

  useEffect(() => {
    if (headerDivRef.current && secondDivRef.current) {
      const height = headerDivRef.current.offsetHeight;
      secondDivRef.current.style.height = `${height}px`;
      searchRef.current!.style.height = `${height}px`;
    }
  }, []);

  useEffect(() => {
    parseCsvFile("/data/pdl.csv").then(setProducts);
    getAllBlogPosts().then(setPosts);
  }, [setProducts, setPosts]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        searchRef.current &&
        !searchRef.current.contains(event.target as Node)
      ) {
        setIsSearchOpen(false);
      }
    };
    if (isSearchOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isSearchOpen]);

  return (
    <>
      <AppBar
        position="fixed"
        elevation={1}
        className="!bg-white/80 backdrop-blur-sm"
      >
        <Toolbar
          ref={headerDivRef}
          disableGutters
          className="px-4 sm:px-6 lg:px-10"
        >
          <Grid container spacing={1} alignItems="center" className="w-full">
            {/* Desktop Menu Button and Logo */}
            <Grid size={{ xs: 2, sm: 2, lg: 2 }} className="flex items-center">
              <IconButton
                size="large"
                onClick={handleDrawerToggle}
                className=""
                sx={{
                  display: {
                    xs: "block", // mobile
                    sm: "none", // từ sm (>=600px) trở lên thì ẩn
                  },
                }}
              >
                <MenuIcon />
              </IconButton>
              <Box
                className="flex items-center space-x-3 cursor-pointer"
                onClick={() => handleCategoryClick("home")}
              >
                <img
                  src="/images/logo.svg"
                  alt="Logo"
                  className="w-10 h-10 min-w-[25px]"
                />
                <Box className="md:hidden sm:flex lg:flex flex-col leading-tight min-w-[150px]">
                  <Typography className="text-base sm:text-lg lg:text-xl font-semibold">
                    <span className="text-primary_blue">SIÊU THỊ </span>
                    <span className="text-black">ĐIỆN LẠNH</span>
                  </Typography>
                  <Typography className="text-gray-500 text-sm hidden md:block">
                    Siêu thị điện lạnh
                  </Typography>
                </Box>
              </Box>
            </Grid>

            {/* Search Bar and Info Section */}
            <Grid
              size={{ xs: 10, sm: 10, lg: 10 }}
              className="flex justify-end"
            >
              <Box className="flex flex-col">
                {/* Top Row: Search Bar and Info */}
                <Box className="flex items-center justify-end py-2">
                  <Box className="hidden md:flex items-center gap-4 lg:gap-10 pr-10">
                    <Box className="flex flex-col items-center">
                      <Typography
                        variant="body1"
                        className="!font-bold text-gray-700"
                      >
                        BẢO HÀNH CHÍNH HÃNG
                      </Typography>
                      <Typography variant="body1" className="text-gray-700">
                        Tận nơi toàn quốc
                      </Typography>
                    </Box>
                    <Box className="hidden xl:flex flex-col items-center">
                      <Typography variant="body1" className="text-gray-700">
                        Open: T2 - CN
                      </Typography>
                      <Typography
                        variant="body1"
                        className="!font-bold text-gray-700"
                      >
                        (08:00 - 17:00)
                      </Typography>
                    </Box>
                  </Box>
                  <Paper
                    component="form"
                    sx={{
                      p: 1,
                      display: "flex",
                      alignItems: "center",
                      boxShadow: {
                        xs: "none", // mobile: không shadow
                        sm: 1, // >= sm: dùng shadow theme.elevations[1]
                      },
                    }}
                    className="flex items-center px-1 py-1 !bg-transparent"
                  >
                    <Box className="flex items-center">
                      <IconButton
                        onClick={handleSearchToggle}
                        className="sm:hidden p-2"
                      >
                        <SearchIcon />
                      </IconButton>

                      <Fade in={isSearchOpen} timeout={300}>
                        <Box
                          className="flex items-center sm:hidden absolute h-[64px] left-0 right-0 top-0 bg-white/80 backdrop-blur-sm p-2 shadow-md z-10"
                          ref={searchRef}
                        >
                          <InputBase
                            id="search-input"
                            startAdornment={
                              <InputAdornment position="start">
                                <SearchIcon />
                              </InputAdornment>
                            }
                            placeholder="Bạn muốn tìm gì?"
                            inputProps={{ "aria-label": "search" }}
                            sx={{ ml: 1, flex: 1 }}
                          />

                          <Button
                            className="hidden lg:flex !text-base !bg-primary_blue !text-white !px-4 !py-2"
                            variant="contained"
                            onClick={() =>
                              handleSearch(
                                (
                                  document.getElementById(
                                    "search-input"
                                  ) as HTMLInputElement
                                ).value
                              )
                            }
                            sx={{
                              textTransform: "none",
                              backgroundColor: COLORS.primary_blue,
                              "&:hover": { backgroundColor: "#2DAE58" },
                            }}
                          >
                            Tìm kiếm
                          </Button>
                        </Box>
                      </Fade>
                      <Box className="hidden sm:flex items-center flex-grow">
                        <InputBase
                          id="search-input"
                          placeholder="Bạn muốn tìm gì?"
                          inputProps={{ "aria-label": "search" }}
                          sx={{
                            ml: 1,
                            flex: 1,
                            minWidth: { xs: "100%", sm: "150px", lg: "200px" },
                          }}
                        />
                      </Box>
                      <Box className="hidden sm:flex items-center">
                        <IconButton
                          onClick={() =>
                            handleSearch(
                              (
                                document.getElementById(
                                  "search-input"
                                ) as HTMLInputElement
                              ).value
                            )
                          }
                          className="p-2"
                        >
                          <SearchIcon />
                        </IconButton>
                        <Divider
                          sx={{ height: 28, m: 0.5 }}
                          orientation="vertical"
                        />
                        <IconButton
                          className="p-2 !text-primary_blue"
                          onClick={handleClick}
                        >
                          <DirectionsIcon />
                        </IconButton>
                        <Divider
                          sx={{ height: 28, m: 0.5 }}
                          orientation="vertical"
                        />
                        <IconButton
                          className="p-2 !text-primary_blue"
                          href={`tel:${PHONE_NUMBER}`}
                        >
                          <WifiCalling3Icon />
                        </IconButton>
                        <Divider
                          sx={{ height: 28, m: 0.5 }}
                          orientation="vertical"
                        />
                        <IconButton
                          className="p-2 !text-primary_blue"
                          href={`mailto:${EMAIL}`}
                        >
                          <MailIcon />
                        </IconButton>
                      </Box>
                    </Box>
                  </Paper>
                </Box>

                {/* Bottom Row: Desktop Menu and Action Button */}
                <Box className="hidden md:flex items-center justify-end w-full py-2">
                  <Box className="flex mr-4">
                    <Stack direction="row" spacing={1}>
                      {categories.map((category) => (
                        <Box key={category.id}>
                          <AnimatedButton
                            onClick={(e) =>
                              category.children
                                ? handleMenuOpen(e, category)
                                : handleCategoryClick(category.id)
                            }
                            className={`!bg-transparent !text-[16px] ${
                              currentCategory.id === category.id
                                ? "!text-primary_blue"
                                : "!text-[#4D4D4D]"
                            }`}
                            sx={{ textTransform: "none" }}
                            endIcon={
                              category.children ? <ArrowDropDownIcon /> : null
                            }
                          >
                            {category.name}
                          </AnimatedButton>
                          {category.children && (
                            <Menu
                              anchorEl={menuAnchorEl}
                              open={
                                Boolean(menuAnchorEl) &&
                                selectedCategory?.id === category.id
                              }
                              onClose={handleMenuClose}
                              anchorOrigin={{
                                vertical: "bottom",
                                horizontal: "left",
                              }}
                              transformOrigin={{
                                vertical: "top",
                                horizontal: "left",
                              }}
                            >
                              {category.children.map((child) => (
                                <MenuItem
                                  key={child.id}
                                  onClick={() => handleCategoryClick(child.id)}
                                >
                                  {child.name}
                                </MenuItem>
                              ))}
                            </Menu>
                          )}
                        </Box>
                      ))}
                    </Stack>
                  </Box>
                  <Button
                    className="hidden lg:flex !text-base !bg-primary_blue !text-white !px-4 !py-2"
                    variant="contained"
                    onClick={handleOpenBooking}
                    sx={{
                      textTransform: "none",
                      backgroundColor: COLORS.primary_blue,
                      "&:hover": { backgroundColor: "#2DAE58" },
                    }}
                  >
                    Đặt lịch
                  </Button>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Toolbar>
      </AppBar>

      {/* Drawer Mobile Menu */}
      <Drawer
        anchor="left"
        open={isDrawerOpen}
        onClose={handleDrawerToggle}
        sx={{
          "& .MuiDrawer-paper": {
            width: 250,
            paddingTop: "56px",
            backgroundColor: "rgba(255, 255, 255, 0.8)",
            backdropFilter: "blur(6px)",
          },
        }}
      >
        <Box
          className="flex items-center space-x-3 cursor-pointer p-4"
          onClick={() => handleCategoryClick(categories[0].id)}
        >
          <img src="/images/logo.svg" alt="Logo" className="w-10 h-10" />
          <Box className="flex flex-col leading-tight">
            <Typography className="text-base sm:text-lg lg:text-xl font-semibold">
              <span className="text-primary_blue">SIÊU THỊ </span>
              <span className="text-black">ĐIỆN LẠNH</span>
            </Typography>
            <Typography className="text-gray-500 text-sm">
              Siêu thị điện lạnh
            </Typography>
          </Box>
        </Box>
        <List>
          {categories.map((category) => (
            <Box key={category.id}>
              <ListItem disablePadding>
                <ListItemButton
                  onClick={() => handleCategoryClick(category.id)}
                >
                  <ListItemText primary={category.name} />
                </ListItemButton>
              </ListItem>
              {category.children && (
                <List component="div" disablePadding>
                  {category.children.map((child) => (
                    <ListItem key={child.id} disablePadding sx={{ pl: 4 }}>
                      <ListItemButton
                        onClick={() => handleCategoryClick(child.id)}
                      >
                        <ListItemText primary={child.name} />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              )}
            </Box>
          ))}
        </List>
      </Drawer>

      {/* Spacer for AppBar height */}
      <Box ref={secondDivRef} />

      {/* Booking Modal */}
      <BookingModal
        open={openBooking}
        handleClose={handleCloseBooking}
        serviceName=""
      />

      {/* Popover for Address */}
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Box className="p-4 bg-white rounded shadow-lg">
          <Typography className="text-gray-800">
            KDC Đức Huy, P.Đông Hoà, TP.Hồ Chí Minh
          </Typography>
        </Box>
      </Popover>
      <Zalo></Zalo>
    </>
  );
};

export default Header;
